import type { SafeInfo, SafeInfoExtended } from "@safe-global/safe-apps-sdk";

import {
  normalizeAddress,
  parseAddressAllowlist,
  resolveApprovedFallbackHandlers,
  resolveApprovedGuards,
  resolveApprovedModules
} from "@/config/runtimeConfig";

export type RuntimeSafeInventoryStatus = "AVAILABLE" | "UNAVAILABLE" | "INVALID";

export type RuntimePostureDowngradeCue =
  | "SAFE_THRESHOLD_SINGLE_OWNER"
  | "SAFE_THRESHOLD_UNKNOWN"
  | "SAFE_MODULE_UNKNOWN"
  | "SAFE_MODULES_UNKNOWN"
  | "SAFE_GUARD_UNKNOWN"
  | "SAFE_FALLBACK_HANDLER_RISKY"
  | "SAFE_FALLBACK_HANDLER_UNKNOWN";

export type RuntimeSafeConfigurationInventory = {
  owners: string[];
  ownersStatus: RuntimeSafeInventoryStatus;
  threshold?: number;
  thresholdStatus: RuntimeSafeInventoryStatus;
  modules: string[];
  modulesStatus: RuntimeSafeInventoryStatus;
  guard?: string | null;
  guardStatus: RuntimeSafeInventoryStatus;
  contractVersion?: string;
  contractVersionStatus: RuntimeSafeInventoryStatus;
  fallbackHandler?: string | null;
  fallbackHandlerStatus: RuntimeSafeInventoryStatus;
};

export type RuntimePostureDowngrade = {
  referenceGrade: boolean;
  cues: RuntimePostureDowngradeCue[];
};

export type RuntimeDowngradePolicy = {
  approvedModules: Set<string>;
  approvedGuards: Set<string>;
  approvedFallbackHandlers: Set<string>;
};

type AddressPolicyInput = string | ReadonlySet<string>;

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

function normalizeOptionalAddress(value: unknown): string | null | undefined {
  if (value === null) return null;
  const normalized = normalizeAddress(value);
  if (!normalized) return undefined;
  return normalized === ZERO_ADDRESS ? null : normalized;
}

export function resolveRuntimeDowngradePolicy(
  input?: Partial<{
    approvedModules: AddressPolicyInput;
    approvedGuards: AddressPolicyInput;
    approvedFallbackHandlers: AddressPolicyInput;
  }>
): RuntimeDowngradePolicy {
  function resolveAddressPolicy(
    value: AddressPolicyInput | undefined,
    fallback: () => Set<string>
  ): Set<string> {
    if (value === undefined) return fallback();
    return typeof value === "string" ? parseAddressAllowlist(value) : new Set(value);
  }

  return {
    approvedModules: resolveAddressPolicy(input?.approvedModules, resolveApprovedModules),
    approvedGuards: resolveAddressPolicy(input?.approvedGuards, resolveApprovedGuards),
    approvedFallbackHandlers: resolveAddressPolicy(
      input?.approvedFallbackHandlers,
      resolveApprovedFallbackHandlers
    )
  };
}

export function resolveSafeConfigurationInventory(params: {
  safeDetected: boolean;
  safe: SafeInfo | undefined;
}): RuntimeSafeConfigurationInventory {
  const { safeDetected, safe } = params;
  const details = safe as Partial<SafeInfoExtended> | undefined;
  const inventory: RuntimeSafeConfigurationInventory = {
    owners: [],
    ownersStatus: "UNAVAILABLE",
    thresholdStatus: "UNAVAILABLE",
    modules: [],
    modulesStatus: "UNAVAILABLE",
    guardStatus: "UNAVAILABLE",
    contractVersionStatus: "UNAVAILABLE",
    fallbackHandlerStatus: "UNAVAILABLE"
  };

  if (!safeDetected) return inventory;

  const ownersRaw = details?.owners;
  if (ownersRaw === undefined) {
    inventory.ownersStatus = "UNAVAILABLE";
  } else if (!Array.isArray(ownersRaw) || ownersRaw.length === 0) {
    inventory.ownersStatus = "INVALID";
  } else {
    const normalizedOwners: string[] = [];
    let ownersInvalid = false;
    for (const owner of ownersRaw) {
      const normalizedOwner = normalizeAddress(owner);
      if (!normalizedOwner) {
        ownersInvalid = true;
        break;
      }
      normalizedOwners.push(normalizedOwner);
    }

    if (ownersInvalid) {
      inventory.ownersStatus = "INVALID";
    } else {
      inventory.ownersStatus = "AVAILABLE";
      inventory.owners = normalizedOwners;
    }
  }

  const thresholdRaw = details?.threshold;
  if (thresholdRaw === undefined) {
    inventory.thresholdStatus = "UNAVAILABLE";
  } else if (!Number.isInteger(thresholdRaw) || thresholdRaw < 1) {
    inventory.thresholdStatus = "INVALID";
  } else {
    const threshold = Number(thresholdRaw);
    if (inventory.ownersStatus === "AVAILABLE" && threshold > inventory.owners.length) {
      inventory.thresholdStatus = "INVALID";
    } else {
      inventory.thresholdStatus = "AVAILABLE";
      inventory.threshold = threshold;
    }
  }

  const modulesRaw = details?.modules;
  if (modulesRaw === undefined || modulesRaw === null) {
    inventory.modulesStatus = "UNAVAILABLE";
  } else if (!Array.isArray(modulesRaw)) {
    inventory.modulesStatus = "INVALID";
  } else {
    const normalizedModules: string[] = [];
    let modulesInvalid = false;
    for (const moduleAddress of modulesRaw) {
      const normalizedModule = normalizeAddress(moduleAddress);
      if (!normalizedModule) {
        modulesInvalid = true;
        break;
      }
      normalizedModules.push(normalizedModule);
    }

    if (modulesInvalid) {
      inventory.modulesStatus = "INVALID";
    } else {
      inventory.modulesStatus = "AVAILABLE";
      inventory.modules = normalizedModules;
    }
  }

  const guardNormalized = normalizeOptionalAddress(details?.guard);
  if (details?.guard === undefined) {
    inventory.guardStatus = "UNAVAILABLE";
  } else if (guardNormalized === undefined) {
    inventory.guardStatus = "INVALID";
  } else {
    inventory.guardStatus = "AVAILABLE";
    inventory.guard = guardNormalized;
  }

  const version = typeof details?.version === "string" ? details.version.trim() : undefined;
  if (!version) {
    inventory.contractVersionStatus = "UNAVAILABLE";
  } else {
    inventory.contractVersionStatus = "AVAILABLE";
    inventory.contractVersion = version;
  }

  const fallbackHandlerNormalized = normalizeOptionalAddress(details?.fallbackHandler);
  if (details?.fallbackHandler === undefined) {
    inventory.fallbackHandlerStatus = "UNAVAILABLE";
  } else if (fallbackHandlerNormalized === undefined) {
    inventory.fallbackHandlerStatus = "INVALID";
  } else {
    inventory.fallbackHandlerStatus = "AVAILABLE";
    inventory.fallbackHandler = fallbackHandlerNormalized;
  }

  return inventory;
}

export function resolveRuntimeDowngrade(params: {
  safeDetected: boolean;
  inventory: RuntimeSafeConfigurationInventory;
  policy: RuntimeDowngradePolicy;
}): RuntimePostureDowngrade {
  const { safeDetected, inventory, policy } = params;
  const cues: RuntimePostureDowngradeCue[] = [];

  if (safeDetected) {
    if (inventory.thresholdStatus !== "AVAILABLE") {
      cues.push("SAFE_THRESHOLD_UNKNOWN");
    } else if (inventory.threshold === 1) {
      cues.push("SAFE_THRESHOLD_SINGLE_OWNER");
    }

    if (inventory.modulesStatus !== "AVAILABLE") {
      cues.push("SAFE_MODULES_UNKNOWN");
    } else if (
      inventory.modules.some((moduleAddress) => !policy.approvedModules.has(moduleAddress))
    ) {
      cues.push("SAFE_MODULE_UNKNOWN");
    }

    if (inventory.guardStatus !== "AVAILABLE") {
      cues.push("SAFE_GUARD_UNKNOWN");
    } else if (
      inventory.guard !== null &&
      inventory.guard !== undefined &&
      !policy.approvedGuards.has(inventory.guard)
    ) {
      cues.push("SAFE_GUARD_UNKNOWN");
    }

    if (inventory.fallbackHandlerStatus !== "AVAILABLE") {
      cues.push("SAFE_FALLBACK_HANDLER_UNKNOWN");
    } else if (
      inventory.fallbackHandler !== null &&
      inventory.fallbackHandler !== undefined &&
      !policy.approvedFallbackHandlers.has(inventory.fallbackHandler)
    ) {
      cues.push("SAFE_FALLBACK_HANDLER_RISKY");
    }
  }

  return {
    referenceGrade: safeDetected && cues.length === 0,
    cues
  };
}
